/** Styleseat Brand Colors.
 * Please update `app/styles/_colors.scss` when adding to this list.
 * Post in #eng-fe-design when modifying this file.
 *
 * Don't forget to update toRGBA if the value is not 'transparent', or not in RGB, RGBA, or hex
 * format.
 *
 * https://www.figma.com/file/AeCdzGXeZEREAhfFxz9Euz/Base-Components?node-id=10302%3A1681&t=QwsYB0FGKplW7l71-0
 */
export enum BrandColor {
  // ------------------------
  // Utility
  // ------------------------
  Transparent = 'transparent',
  DropShadow = 'rgba(203, 203, 203, 0.5)',
  Veil = 'rgba(18, 17, 17, 0.5)',
  DeepVeil = 'rgba(18, 17, 17, 0.75)',

  // ------------------------
  // Brand Colors
  // ------------------------
  WhiteWash = '#ffffff',
  BackInBlack = '#121111',
  Chill = '#e0f3fd',
  Sunshine = '#f2c94c',
  Punch = '#E10984',
  Pounce = '#FF487F',
  Spark = '#1ae3e5',
  Slate = '#2d5d7c',
  Lime = '#EEFBF3',
  ItsOff = '#f3f0e2',
  CreamCorn = '#f9f7f0',
  CreamSpinach = '#f7fffa',
  WarningBackground = '#fef9ed',

  Petal = '#e3bab3',
  Smudge = '#687672',
  Blue = '#2d9cdb',
  LightChill = '#f2f8fb',

  // Brand Colors - Supplemental
  Punch2 = '#fa84c8',
  Punch3 = '#fca9d9',
  Punch4 = '#fdcbe8',
  Punch5 = '#feeef7',
  CreamCorn2 = '#fffdf6',
  CreamCorn3 = '#fffdf7',
  Golden = '#d6a100',

  // ------------------------
  // Grays
  // ------------------------
  Mud = '#242626',
  Metal = '#494949',
  Ash = '#7a7d7c',
  Silver = '#c3c7c5',
  LightGrey = '#f2f2f2',
  SuperLight = '#fafafa',

  // ------------------------
  // UI Colors
  // ------------------------
  Red = '#b00020',
  RedLite = '#fbeae5',
  Green = '#30a55e',
  LightGreen = '#eefbf3',

  // ------------------------
  // Smart Pricing
  // ------------------------
  LightPurple = '#f5effa',
  Purple = '#2c2ba7',

  // ------------------------
  // Other
  // ------------------------
  InstagramBlue = '#2e5e86',
  TwitterBlue = '#64cbee',
  FacebookBlue = '#3a589a',

  // Other Grays
  Grey5 = '#a9acab',
  Grey3 = '#dbdedd',
  Grey2 = '#E9ECEB',
  Grey1 = '#F2F5F5',
}

type RGBColor = `rgb(${string},${string},${string})`;
type RGBAColor = `rgba(${string},${string},${string},${string})`;

export type Color = BrandColor | RGBColor | RGBAColor;

type BackwardsCompatibleColorNames = Uncapitalize<keyof typeof BrandColor | 'blackInBlack'>;

/** @deprecated please use BrandColor instead */
export const colors: Record<BackwardsCompatibleColorNames, BrandColor> = {
  transparent: BrandColor.Transparent,
  dropShadow: BrandColor.DropShadow,
  veil: BrandColor.Veil,
  deepVeil: BrandColor.DeepVeil,
  whiteWash: BrandColor.WhiteWash,
  /** @deprecated please use backInBlack instead */
  blackInBlack: BrandColor.BackInBlack,
  backInBlack: BrandColor.BackInBlack,
  chill: BrandColor.Chill,
  sunshine: BrandColor.Sunshine,
  punch: BrandColor.Punch,
  pounce: BrandColor.Pounce,
  spark: BrandColor.Spark,
  slate: BrandColor.Slate,
  lime: BrandColor.Lime,
  itsOff: BrandColor.ItsOff,
  creamCorn: BrandColor.CreamCorn,
  creamSpinach: BrandColor.CreamSpinach,
  warningBackground: BrandColor.WarningBackground,
  petal: BrandColor.Petal,
  smudge: BrandColor.Smudge,
  blue: BrandColor.Blue,
  lightChill: BrandColor.LightChill,
  punch2: BrandColor.Punch2,
  punch3: BrandColor.Punch3,
  punch4: BrandColor.Punch4,
  punch5: BrandColor.Punch5,
  creamCorn2: BrandColor.CreamCorn2,
  creamCorn3: BrandColor.CreamCorn3,
  golden: BrandColor.Golden,
  mud: BrandColor.Mud,
  metal: BrandColor.Metal,
  ash: BrandColor.Ash,
  silver: BrandColor.Silver,
  lightGrey: BrandColor.LightGrey,
  superLight: BrandColor.SuperLight,
  red: BrandColor.Red,
  redLite: BrandColor.RedLite,
  green: BrandColor.Green,
  lightGreen: BrandColor.LightGreen,
  lightPurple: BrandColor.LightPurple,
  purple: BrandColor.Purple,
  instagramBlue: BrandColor.InstagramBlue,
  twitterBlue: BrandColor.TwitterBlue,
  facebookBlue: BrandColor.FacebookBlue,
  grey5: BrandColor.Grey5,
  grey3: BrandColor.Grey3,
  grey2: BrandColor.Grey2,
  grey1: BrandColor.Grey1,
};

/**
 * Parses a hex color and returns an array containing each color value
 * in R,G,B order.
 * @param hex - 6 digit hex value (eg: #FF99CC)
 */
const hexToRGBParts = (hex: string): string[] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result.slice(1).map(h => parseInt(h, 16).toString());
};

/**
 * **Contact design if using a color that's not in BrandColor**
 *
 * Convert a hex color representation to its corresponding rgb() representation.
 * @param {string} hex - 6 digit hex value (eg: #FF99CC)
 */
export function toRGB(hex: string): RGBColor {
  const parts = hexToRGBParts(hex);
  return `rgb(${parts[0]},${parts[1]},${parts[2]})`;
}

const RGB_OR_RGBA_REGEX = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:|,\s*([0-9.]+)\s*)\)$/;
const isTransparent = (
  color: Color,
): color is BrandColor.Transparent => color === BrandColor.Transparent;
/**
 * Convert a hex color representation to its corresponding rgba() representation.
 *
 * @param {Color} color
 * @param {name} opacity - the opacity value (0 to 1) to attach to the rgba result; If color is
 *  already rgba, this overrides the opacity on that color.
 */
export function toRGBA(color: Color, opacity: number | string): RGBAColor {
  let matches: string[];
  if (isTransparent(color)) {
    return 'rgba(0,0,0,0)';
  }

  if (color.startsWith('#')) {
    matches = hexToRGBParts(color);
  } else if (color.startsWith('rgb')) {
    matches = color.match(RGB_OR_RGBA_REGEX);
  } else {
    throw new TypeError(`Unsupported color: ${color}`);
  }

  const parts = [
    matches[1],
    matches[2],
    matches[3],
    opacity.toString(),
  ];
  return `rgba(${parts[0]},${parts[1]},${parts[2]},${parts[3]})`;
}
